import { Cancel, CheckCircle } from '@mui/icons-material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Alert, Tooltip } from '@mui/material';
import { Fragment, useMemo, useState } from 'react';
import ReactPaginate from 'react-paginate';
import { Link } from 'react-router-dom';
import { translate } from 'react-switch-lang';
import { Card, CardBody, Col, Container, Row } from 'reactstrap';
import { InfoButtonIcon } from 'src/component/Buttons/InfoButtonIcon';
import { useFilterCountries } from 'src/hooks/useFilterCountries';
import { useFilterProjects } from 'src/hooks/useFilterProjects';
import { useProductionCycles } from 'src/hooks/useProductionCycles';
import { parseProductionCyclePath } from 'src/utils/utilMethods';
import Breadcrumb from '../../../breadcrumb';
import styles from '../../../styles/style-setting.module.scss';
import ProductionCycleColumns from './table-header/production-cycle-columns';

const PAGE_SIZE = 10;

const ProductionCyclePage = (props) => {
  // ── Filter state ─────────────────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [countryId, setCountryId] = useState<number | null>(null);
  const [projectId, setProjectId] = useState<number | null>(null);
  const [tpeId, setTpeId] = useState<number | null>(null);
  const [productionId, setProductionId] = useState<number | null>(null);
  const [page, setPage] = useState(0);

  const { data, loading, error, options } = useProductionCycles({
    search, countryId, projectId, tpeId, productionId, page, size: PAGE_SIZE,
  });
  const { projects } = useFilterProjects();
  const { countries } = useFilterCountries();

  // ── Cascade handlers ─────────────────────────────────────────────────────────
  const handleProjectChange = (value: number | null) => {
    setProjectId(value);
    setTpeId(null);        // reset downstream
    setProductionId(null); // reset downstream
    setPage(0);
  };

  const handleTpeChange = (value: number | null) => {
    setTpeId(value);
    setProductionId(null); // reset downstream
    setPage(0);
  };

  const isAnyFilterActive =
    !!search || countryId != null || projectId != null || tpeId != null || productionId != null;

  const resetFilters = () => {
    setSearch('');
    setCountryId(null);
    setProjectId(null);
    setTpeId(null);
    setProductionId(null);
    setPage(0);
  };

  // ── Table rows ────────────────────────────────────────────────────────────────
  const rows = useMemo(
    () =>
      data.content.map((pc) => ({
        projet: pc.tpe?.project?.name || '-',
        tpe: pc.tpe?.name || '-',
        production: pc.production?.name || '-',
        label: parseProductionCyclePath(pc.label).productionCycleLabel,
        mode: pc.mode,
        startDate: pc.startDate ? new Date(pc.startDate).toLocaleDateString() : '-',
        endDate: pc.endDate ? new Date(pc.endDate).toLocaleDateString() : '-',
        active: (
          <Tooltip title={pc.active ? props.t('yes') : props.t('no')}>
            {pc.active ? (
              <CheckCircle sx={{ color: 'green', fontSize: 24 }} />
            ) : (
              <Cancel sx={{ color: 'red', fontSize: 24 }} />
            )}
          </Tooltip>
        ),
        action: (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center' }}>
            <Link to={`${process.env.PUBLIC_URL}/settings/production-cycle/${pc.id}`}>
              <InfoButtonIcon title={props.t('more_details')} className={`fa fa-eye ${styles['action']}`} />
            </Link>
            <Link to={`${process.env.PUBLIC_URL}/settings/production-cycle/edit/${pc.id}`}>
              <Tooltip title={props.t('production_cycle.edit_title')}>
                <EditOutlinedIcon
                  sx={{ cursor: 'pointer', color: '#2196f3', fontSize: 22, '&:hover': { color: '#1976d2' } }}
                />
              </Tooltip>
            </Link>
          </div>
        ),
      })),
    [data.content]
  );

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <Fragment>
      <Breadcrumb parent={props.t('production_cycle.list')} url="settings/production-cycle" />
      <Container fluid={true}>
        <Row>
          <Col sm="12">
            <Card>
              <CardBody>

                {/* ── Filter panel ── */}
                <div className={styles['filterPanel']}>

                  <div className={styles['filterHeader']}>
                    <span className={styles['filterTitle']}>
                      {props.t('filter.title')}
                    </span>
                    {isAnyFilterActive && (
                      <button
                        type="button"
                        className={styles['resetBtn']}
                        onClick={resetFilters}
                      >
                        ↺ {props.t('filter.reset')}
                      </button>
                    )}
                  </div>

                  <div className={styles['filterBody']}>
                    <div className="row">

                      {/* Search — wider on large screens */}
                      <div className="col-12 col-sm-6 col-lg-3 mb-3">
                        <label className={styles['filterLabel']}>
                          {props.t('production_cycle.search')}
                        </label>
                        <input
                          className="form-control form-control-sm"
                          value={search}
                          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                          placeholder="…"
                          disabled={loading}
                        />
                      </div>

                      {/* Country */}
                      <div className="col-6 col-sm-3 col-lg mb-3">
                        <label className={styles['filterLabel']}>
                          {props.t('tpe.country')}
                        </label>
                        <select
                          className="form-control form-control-sm"
                          value={countryId ?? ''}
                          onChange={(e) => { setCountryId(e.target.value ? Number(e.target.value) : null); setPage(0); }}
                          disabled={loading}
                        >
                          <option value="">{props.t('filter.allCountries')}</option>
                          {countries.map((c) => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                          ))}
                        </select>
                      </div>

                      {/* Project */}
                      <div className="col-6 col-sm-3 col-lg mb-3">
                        <label className={styles['filterLabel']}>
                          {props.t('production_cycle.project')}
                        </label>
                        <select
                          className="form-control form-control-sm"
                          value={projectId ?? ''}
                          onChange={(e) => handleProjectChange(e.target.value ? Number(e.target.value) : null)}
                          disabled={loading}
                        >
                          <option value="">{props.t('filter.allProjects')}</option>
                          {projects.map((p) => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                      </div>

                      {/* TPE — locked until project is selected */}
                      <div className="col-6 col-sm-3 col-lg mb-3">
                        <label className={`${styles['filterLabel']} ${projectId == null ? styles['filterLabelDimmed'] : ''}`}>
                          {props.t('production_cycle.tpe')}
                        </label>
                        <select
                          className={`form-control form-control-sm ${projectId == null ? styles['filterSelectDimmed'] : ''}`}
                          value={tpeId ?? ''}
                          onChange={(e) => handleTpeChange(e.target.value ? Number(e.target.value) : null)}
                          disabled={loading || projectId == null}
                        >
                          <option value="">{props.t('filter.allTpes')}</option>
                          {options.tpes.map((t) => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                          ))}
                        </select>
                      </div>

                      {/* Production — locked until TPE is selected */}
                      <div className="col-6 col-sm-3 col-lg mb-3">
                        <label className={`${styles['filterLabel']} ${tpeId == null ? styles['filterLabelDimmed'] : ''}`}>
                          {props.t('production_cycle.production')}
                        </label>
                        <select
                          className={`form-control form-control-sm ${tpeId == null ? styles['filterSelectDimmed'] : ''}`}
                          value={productionId ?? ''}
                          onChange={(e) => { setProductionId(e.target.value ? Number(e.target.value) : null); setPage(0); }}
                          disabled={loading || tpeId == null}
                        >
                          <option value="">{props.t('filter.allProductions')}</option>
                          {options.productions.map((p) => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                      </div>

                    </div>
                  </div>

                  <div className={styles['filterFooter']}>
                    <small className="text-muted">
                      {data.totalElements} {props.t('filter.results')}
                    </small>
                  </div>

                </div>
                {/* ── end filter panel ── */}

                {error && (
                  <Alert variant="outlined" severity="error" className="mb-3">
                    {error}
                  </Alert>
                )}

                {loading ? (
                  <div className="text-center p-4">
                    <i className="fa fa-spin fa-spinner fa-2x" />
                  </div>
                ) : rows.length === 0 ? (
                  <Alert variant="outlined" severity="info">
                    {props.t('production_cycle.no_data')}
                  </Alert>
                ) : (
                  <ProductionCycleColumns data={rows} />
                )}

                <div className="d-flex justify-content-end mt-3">
                  {data.totalPages > 1 && (
                    <ReactPaginate
                      previousLabel="<"
                      nextLabel=">"
                      pageCount={data.totalPages}
                      onPageChange={({ selected }) => setPage(selected)}
                      forcePage={page}
                      containerClassName={styles['paginationButton']}
                      previousLinkClassName={styles['previousBtn']}
                      nextLinkClassName={styles['nextBtn']}
                      disabledClassName={styles['paginationDisabled']}
                      activeClassName={styles['paginationActive']}
                    />
                  )}
                </div>

              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default translate(ProductionCyclePage);
